/**
 * @module app.shared
 */

import { bindingMode, autoinject, processContent, computedFrom, TaskQueue } from 'aurelia-framework';
import flatpickr from 'flatpickr';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { bindable } from 'aurelia-typed-observable-plugin';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { DateTimeValue } from 'app/shared/lib/date-time';
import { updateMaterializeInputLabel } from 'app/shared/lib/materialize/dom-utilities';
import { guid } from 'app/shared/lib/guid';
import { getKey, Key } from 'app/shared/lib/keys';
import { microTask, defer } from 'app/shared/lib/decorators';
import { moveRulesToHelperSlot } from 'app/shared/elements/ess-rule';
import { IInput } from 'app/shared/elements/forms/input-base';
import { parse } from 'date-fns';
import { deferEvent } from 'app/shared/lib/fire-events';
import { BaseOptions } from 'flatpickr/dist/types/options';
import { I18N } from 'aurelia-i18n';
import { focusNext, focusPrevious } from 'app/shared/lib/focus';
import { AppLogger } from 'app/shared/lib/logging/app-logger';

@autoinject()
@processContent(moveRulesToHelperSlot)
export class EssDatetimeCustomElement implements IInput {

    ////****************** Fields and Properties **************************************////
    protected id: guid = guid();

    @bindable.stringNull({ defaultBindingMode: bindingMode.twoWay })
    public value: string | null = null;

    @bindable.stringNull()
    public requiredValidatorLabel: string | null = null;

    @bindable.string()
    public testName: string = '';

    @bindable.stringNull({ changeHandler: 'initializePicker' })
    public minDate: string | null = null;

    @bindable.stringNull({ changeHandler: 'initializePicker' })
    public maxDate: string | null = null;

    @bindable.stringNull({ changeHandler: 'initializePicker' })
    public minTime: string | null = null;

    @bindable.stringNull({ changeHandler: 'initializePicker' })
    public maxTime: string | null = null;

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public use24hour: boolean = false;

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public useSeconds: boolean = false;

    @bindable.stringNull()
    public label: string | null = null;

    @bindable.stringNull()
    public placeholder: string | null = null;

    @bindable.string()
    public autocomplete: string = 'off';

    @bindable.booleanAttr()
    public required: boolean = false;

    @bindable.booleanAttr()
    public readonly: boolean = false;

    @bindable.booleanAttr()
    public disabled: boolean = false;

    @bindable()
    public labelledbyId: string | null = null;  //An element's `id`. Sets the component's `aria-labelledby

    private instance: flatpickr.Instance | null = null;
    private isChanging: boolean = false;
    protected pickerId: string | null = null;
    protected onCloseFollowup: Function | null = null;
    protected deferEvent = deferEvent;

    @computedFrom('label', 'requiredValidatorLabel')
    protected get requiredValueMessage() {
        return this.requiredValidatorLabel || this.i18n.tr('ui:validation:required', { what: this.i18n.tr(this.label || 'ui:labels.value') });
    }

    ////****************** Element References ***************************************////
    protected readonly input!: HTMLInputElement;
    protected readonly element!: HTMLElement;
    protected readonly labelElement!: HTMLLabelElement;

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(private readonly formatter: FormatProvider, private readonly i18n: I18N, private readonly env: EnvironmentService, protected readonly logger: AppLogger,
        protected queue: TaskQueue) { }

    protected attached(): void {
        this.initializePicker();
        this.placeholderChanged();
    }

    protected detached(): void {
        this.destroyPicker();
    }

    ////****************** Events ***************************************************////
    protected handlePickerKeydown(event: KeyboardEvent): boolean {
        const key = getKey(event) as Key;
        let preventDefault = false;
        if(key === Key.Tab) {
            const overrideTabBehavior = () => {
                event.preventDefault();
                event.stopPropagation();
                preventDefault = true;
            };

            const focusedItemClassList = document.activeElement?.classList;
            if(event.shiftKey) {
                if(focusedItemClassList?.contains('flatpickr-day')) {
                    overrideTabBehavior();
                    this.instance?.close();
                    focusPrevious(this.input);
                } else if(focusedItemClassList?.contains('flatpickr-hour')) {
                    overrideTabBehavior();
                    this.focusDay();
                }
            } else {
                if(focusedItemClassList?.contains('flatpickr-am-pm')) {
                    overrideTabBehavior();
                    this.instance?.close();
                    focusNext(this.input);
                } else if(focusedItemClassList?.contains('flatpickr-day')) {
                    overrideTabBehavior();
                    this.focusTime();
                }
            }
        }

        return !preventDefault;  // turn off Aurelia's automatic preventDefault() on events handled with delegate or trigger binding.
    }

    protected handleKeyDown(event: KeyboardEvent): boolean {
        // improving the lives of keyboard users
        const key = getKey(event);
        switch(key) {
            case Key.Down:
            case Key.Enter:
            case Key.Space:
                this.input.blur(); // prevent the input field from becoming the active element by the down key
                event.stopPropagation();  // prevent flatpickr's handling of "enter" on the input (as well as default focusing behavior)
                event.preventDefault();
                // ensure calendar is updated after manual entry occurs
                if(this.instance) {
                    const dateValue = DateTimeValue.fromNativeDate(new Date(this.instance._input.value), this.env.displayTimeZone).toJSON();
                    if(dateValue) this.queue.queueTask(() => this.updateValue(dateValue));
                    else this.queue.queueTask(() => this.clearValue());
                }
                this.queue.queueTask(() => this.open());
                return false;
            case Key.Delete:
                this.clearValue();
                return false;
            default:
                return true;
        }
    }

    protected handleFocusOut(): true {

        // INFO [Matt] Essentially copying the the internal logic of flatpickr on
        // enter key press to the case where focus is lost, e.g. tabs. See
        // https://github.com/flatpickr/flatpickr/blob/v4.6.2/src/index.ts#L1593.
        if(this.instance) {
            this.instance.setDate(
                this.instance._input.value,
                true,
                this.instance.config.dateFormat
            );
        }
        return true;
    }

    protected handleInputMouseEvent(event: MouseEvent): boolean {
        event.stopPropagation();  // prevent flatpickr's handling of click or mousedown on the input
        this.toggle();
        return false;
    }

    protected valueChanged(newValue: string | null, oldValue: string | null): void {
        if(this.instance && (newValue !== oldValue)) {
            this.updateValue();
        }
    }

    private onChange(selectedDates: Date[]): void {
        if(!selectedDates.length) {
            this.synchronizeValue(null);
        }
        else {
            const value = DateTimeValue.fromNativeDate(selectedDates[0], this.env.displayTimeZone).toJSON();
            this.synchronizeValue(value);
        }

        this.queueUpdateLabel();
    }

    protected placeholderChanged(): void {
        this.queueUpdateLabel();
    }

    private onOpen(): void {
        this.input.classList.add('ess-open');
        this.input.setAttribute('aria-expanded', 'true');

        if(this.instance) {
            // move label out of way when popup opens
            if(!this.value && !this.placeholder) {
                this.input.setAttribute('placeholder', ' ');
            }

            this.focusDay();
        }
    }

    private onClose(): void {
        this.input.classList.remove('ess-open');
        this.input.setAttribute('aria-expanded', 'false');

        if(!this.placeholder) {
            // return label to original place
            this.input.setAttribute('placeholder', '');
        }

        if(this.onCloseFollowup) {
            this.onCloseFollowup();
            this.onCloseFollowup = null;
        }
    }

    ////****************** Functions ************************************************////
    protected destroyPicker() {
        if(this.instance) {
            if(this.instance.calendarContainer) {
                this.instance.calendarContainer.removeEventListener('keydown', this.handlePickerKeydown, { capture: true });
            }
            this.instance.destroy();
            this.instance = null;
        }
    }

    protected initializePicker() {

        // INFO [Matt] Called by the change handlers before element is attached,
        // so we need to check that the element is attached.
        if(!this.element) {
            return;
        }

        const minDate = parse(this.minDate || '', 'yyyy-MM-dd', 0);
        if(minDate) {
            minDate.setHours(0);
            minDate.setMinutes(0);
            minDate.setSeconds(0);
            minDate.setMilliseconds(0);
        }
        const maxDate = parse(this.maxDate || '', 'yyyy-MM-dd', 0);
        if(maxDate) {
            maxDate.setHours(23);
            maxDate.setMinutes(59);
            maxDate.setSeconds(59);
            maxDate.setMilliseconds(999);
        }
        const onChange = this.onChange.bind(this);
        const onClose = this.onClose.bind(this);
        const onOpen = this.onOpen.bind(this);
        const formatDate = this.formatDate.bind(this);
        const options: Partial<BaseOptions> = {
            allowInput: true,
            enableTime: true,
            clickOpens: false,
            minDate: isNaN(Number(minDate)) ? undefined : minDate,
            maxDate: isNaN(Number(maxDate)) ? undefined : maxDate,
            enableSeconds: this.useSeconds,
            time_24hr: this.use24hour,
            wrap: true,
            onOpen,
            onChange,
            onClose: [onChange, onClose],
            disableMobile: true,
            ariaDateFormat: 'aria',
            parseDate: (s: string, f: string) => new Date(s),
            formatDate,
        };

        if(this.minTime) {
            options.minTime = this.minTime;
        }
        if(this.maxTime) {
            options.maxTime = this.maxTime;
        }

        this.instance = flatpickr(this.element, options);

        if(this.instance && this.instance.calendarContainer) {
            this.pickerId = this.instance.calendarContainer.getAttribute('id');
            if(!this.pickerId) {
                this.pickerId = `${this.id}-picker`;
                this.instance.calendarContainer.setAttribute('id', this.pickerId);
            }
            // Attach a listener to control when a user tabs out of the container
            this.instance.calendarContainer.addEventListener('keydown', this.handlePickerKeydown.bind(this), { capture: true });
        }

        this.updateValue();
    }

    protected open(): void {
        if(this.instance && !this.readonly) {
            this.instance.open();
        }
    }

    protected toggle(): void {
        if(this.instance && !this.readonly) {
            if(this.instance.isOpen) {
                this.onCloseFollowup = () => {
                    // Transition focus back to the input, when a UIEvent on the input causes the close
                    this.input.focus({ preventScroll: true });
                }
            }
            this.instance.toggle();
        }
    }

    private updateValue(newValue?: string): void {
        if(!this.isChanging && this.instance) {
            const dateTimeValue = new DateTimeValue(newValue || this.value || '');
            if(dateTimeValue.isValid()) {
                const nativeDate = DateTimeValue.toNativeDate(dateTimeValue, this.env.displayTimeZone);
                this.instance.setDate(nativeDate, false); // don't fire change
            }
            else {
                this.instance.clear(false); // don't fire change
            }

            this.queueUpdateLabel();
        }
    }

    protected clearValue(): void {
        if(this.readonly) { return; }
        if(this.instance) {
            this.instance.clear(false); // don't fire change
        }
        this.value = null;
    }

    @microTask
    private synchronizeValue(value: string | null): void {
        // bindable values update asynchronously, so run this in a microtask
        try {
            // prevent infinite recursion
            this.isChanging = true;
            this.value = value;
        }
        finally {
            this.isChanging = false;
        }
    }

    @defer
    private queueUpdateLabel(): void {
        if(this.input && this.labelElement) {
            updateMaterializeInputLabel(this.input, this.labelElement);
        }
    }

    private formatDate(date: Date, format: string): string {
        const value = DateTimeValue.fromNativeDate(date, this.env.displayTimeZone);
        if(format === 'aria') {
            return this.formatter.formatDate(value, { format: 'aria' }) || '';
        }
        return this.formatter.formatDateTime(value) || '';
    }

    private focusDay() {
        if(this.instance) {
            // set focus to the calendar (selected day and current day elements are prioritized)
            const days = this.instance.days;
            if(days) {
                // TODO [Matt] Need to cast as an any to allow building on the build server.
                const selectedDay = <any>days.querySelector('.flatpickr-day.selected:not(.flatpickr-disabled)');
                const today = <any>days.querySelector('.flatpickr-day.today:not(.flatpickr-disabled)');
                const firstAvailableDay = <any>days.querySelector('.flatpickr-day:not(.flatpickr-disabled)');
                if(selectedDay) {
                    selectedDay.focus({ preventScroll: true });
                } else if(today) {
                    today.focus({ preventScroll: true });
                } else if(firstAvailableDay) {
                    firstAvailableDay.focus({ preventScroll: true });
                } else if(this.instance.calendarContainer) {
                    (<any>this.instance.calendarContainer).focus({ preventScroll: true });
                }
            }
        }
    }

    private focusTime() {
        const hour = this.instance?.hourElement;
        if(hour) {
            hour.focus();
        }
    }

}
